import React from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Phone, MapPin, Globe, ChevronRight, Building2
} from 'lucide-react';

const CATEGORY_COLORS = {
  "Food Security": "bg-amber-100 text-amber-800 border-amber-200",
  "Housing & Shelter": "bg-blue-100 text-blue-800 border-blue-200",
  "Healthcare Access": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Mental Health": "bg-purple-100 text-purple-800 border-purple-200",
  "Substance Use": "bg-rose-100 text-rose-800 border-rose-200",
  "Legal / Immigration": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "Employment / Income": "bg-teal-100 text-teal-800 border-teal-200",
  "Transportation": "bg-cyan-100 text-cyan-800 border-cyan-200",
  "Education / ESL": "bg-orange-100 text-orange-800 border-orange-200",
  "Childcare / Family Support": "bg-pink-100 text-pink-800 border-pink-200",
  "Violence / Safety": "bg-red-100 text-red-800 border-red-200",
  "Digital Access": "bg-slate-100 text-slate-800 border-slate-200",
  "Utilities": "bg-yellow-100 text-yellow-800 border-yellow-200"
};

export const getCategoryColor = (category) => {
  return CATEGORY_COLORS[category] || "bg-gray-100 text-gray-800 border-gray-200";
};

export const ResourceCard = ({ 
  resource, 
  isSelected, 
  selectionMode, 
  onToggleSelection, 
  onNavigate, 
  getResourceName, 
  getDisplayText, 
  freeLabel 
}) => {
  return (
    <Card 
      data-testid={`resource-card-${resource.id}`}
      className={`resource-card bg-white rounded-2xl border cursor-pointer group warm-shadow warm-shadow-hover transition-all ${
        isSelected 
          ? 'border-[#5C4023] ring-2 ring-[#5C4023]/20' 
          : 'border-[#E0D6C8] hover:border-[#5C4023]/30'
      }`}
      onClick={() => {
        if (selectionMode) {
          onToggleSelection(resource.id);
        } else {
          onNavigate(`/resource/${resource.id}`);
        }
      }}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          {selectionMode && (
            <div className="mr-3 mt-0.5">
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => onToggleSelection(resource.id)}
                data-testid={`select-resource-${resource.id}`}
                className="data-[state=checked]:bg-[#5C4023] data-[state=checked]:border-[#5C4023]"
              />
            </div>
          )}
          <div className="flex-1">
            <h3 className="font-semibold text-[#1A1A1A] font-['Manrope'] group-hover:text-[#5C4023] transition-colors line-clamp-1">
              {getResourceName(resource)}
            </h3>
            <div className="flex items-center text-sm text-[#5A5A5A] mt-1">
              <Building2 className="w-3.5 h-3.5 mr-1.5" />
              <span className="line-clamp-1">{resource.organization}</span>
            </div>
          </div>
          {!selectionMode && (
            <ChevronRight className="w-5 h-5 text-[#888888] group-hover:text-[#5C4023] transition-colors flex-shrink-0" />
          )}
        </div>
        
        <div className="flex flex-wrap gap-1.5 mb-3">
          <Badge className={`${getCategoryColor(resource.primary_sdoh_category)} text-xs rounded-full border`}>
            {resource.primary_sdoh_category}
          </Badge>
          {resource.cost_to_client === 'Free' && (
            <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 text-xs rounded-full border">
              {freeLabel}
            </Badge>
          )}
        </div>
        
        <p className="text-sm text-[#5A5A5A] line-clamp-2 mb-3">
          {getDisplayText(resource, 'client_facing_description')}
        </p>
        
        <div className="flex flex-wrap gap-3 text-xs text-[#888888]">
          {resource.phone_number && (
            <div className="flex items-center">
              <Phone className="w-3 h-3 mr-1" />
              <span>{resource.phone_number}</span>
            </div>
          )}
          {resource.physical_address && (
            <div className="flex items-center">
              <MapPin className="w-3 h-3 mr-1" />
              <span className="line-clamp-1">{resource.neighborhoods_served?.[0] || 'Multiple'}</span>
            </div>
          )}
          {resource.website && (
            <div className="flex items-center">
              <Globe className="w-3 h-3 mr-1" />
              <span>Website</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const SelectionBar = ({ count, onPrint, onClear, extraAction }) => {
  if (count === 0) return null;
  
  return (
    <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
      <div className="bg-[#1A1A1A] text-white rounded-full px-6 py-3 shadow-2xl flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="font-medium">{count} selected</span>
        </div>
        <div className="w-px h-6 bg-white/20" />
        {extraAction}
        <Button
          onClick={onPrint}
          data-testid="print-selected-btn"
          className="bg-[#5C4023] hover:bg-[#4A331C] text-white rounded-full px-4 h-9"
        >
          Create Resource List
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="text-white/70 hover:text-white hover:bg-white/10 rounded-full"
        >
          Clear
        </Button>
      </div>
    </div>
  );
};

export const LanguageBar = ({ languages, currentLanguage, translating, onChange }) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E0D6C8] z-40 shadow-lg">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-[#5A5A5A]">
          <span className="hidden sm:inline">Resource List language:</span>
          <span className="sm:hidden">Language:</span>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          {Object.entries(languages).map(([code, name]) => (
            <Button
              key={code}
              variant={currentLanguage === code ? "default" : "outline"}
              size="sm"
              onClick={() => onChange(code)}
              disabled={translating}
              data-testid={`lang-${code}`}
              className={`rounded-full text-xs whitespace-nowrap ${
                currentLanguage === code 
                  ? 'bg-[#5C4023] hover:bg-[#4A331C] text-white' 
                  : 'border-[#E0D6C8] hover:border-[#5C4023] hover:text-[#5C4023]'
              }`}
            >
              {name}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};
